import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';
import OrdersService from '../../../services/orders/service';
import EngagementService from '../../../services/engagement/service';

export const useOrders = () => {
    const [unfulfilledOrders, setUnfulfilledOrders] = useState([]);
    const [fulfilledOrders, setFulfilledOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const fetchOrders = useCallback(async () => {
        setLoading(true);
        try {
            const [unfulfilledData, fulfilledData, unfulfilledEngagements, fulfilledEngagements] = await Promise.all([
                OrdersService.fetchUnfulfilledOrders(),
                OrdersService.fetchFulfilledOrders(),
                EngagementService.fetchUnfulfilledOrders(),
                EngagementService.fetchFulfilledOrders()
            ]);

            // Format engagement orders to match the actual data structure
            const formattedUnfulfilledEngagements = unfulfilledEngagements.map(order => ({
                _id: order._id,
                customerName: order.customerName,
                customerEmail: order.customerEmail,
                amount: order.amount,
                productName: order.productName,
                createdAt: order.createdAt,
                status: order.status,
                estimatedCompletion: null,
                customerNotes: order.customerNotes,
                orderType: 'engagement',
                features: order.features,
                // Direct field mapping as they come from backend
                followPlatform: order.followPlatform,
                followUsername: order.followUsername,
                likesPostLink: order.likesPostLink,
                commentsPlatform: order.commentsPlatform,
                commentsPostLink: order.commentsPostLink,
                shoutoutType: order.shoutoutType,
                shoutoutImage: order.shoutoutImage,
                customDetails: order.customDetails
            }));

            const formattedFulfilledEngagements = fulfilledEngagements.map(order => ({
                _id: order._id,
                customerName: order.customerName,
                customerEmail: order.customerEmail,
                amount: order.amount,
                productName: order.productName,
                createdAt: order.createdAt,
                status: order.status,
                fulfillmentDate: order.fulfillmentDate,
                notes: order.notes,
                orderType: 'engagement',
                features: order.features,
                // Direct field mapping as they come from backend
                followPlatform: order.followPlatform,
                followUsername: order.followUsername,
                likesPostLink: order.likesPostLink,
                commentsPlatform: order.commentsPlatform,
                commentsPostLink: order.commentsPostLink,
                shoutoutType: order.shoutoutType,
                shoutoutImage: order.shoutoutImage,
                customDetails: order.customDetails
            }));

            setUnfulfilledOrders([...unfulfilledData, ...formattedUnfulfilledEngagements]);
            setFulfilledOrders([...fulfilledData, ...formattedFulfilledEngagements]);
        } catch (error) {
            message.error('Failed to fetch orders');
            console.error('Error fetching orders:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Rest of the code remains the same
    const updateOrderStatus = async (orderId, data) => {
        try {
            const order = [...unfulfilledOrders, ...fulfilledOrders].find(o => o._id === orderId);
            
            if (order.orderType === 'engagement') {
                await EngagementService.updateEngagementFulfillment(orderId, data);
            } else {
                await OrdersService.updateOrderFulfillment(orderId, data);
            }
            
            message.success('Order status updated successfully');
            await fetchOrders();
        } catch (error) {
            message.error('Failed to update order status');
            console.error('Error updating order:', error);
        }
    };

    const getOrderDetails = async (orderId) => {
        try {
            const order = [...unfulfilledOrders, ...fulfilledOrders].find(o => o._id === orderId);
            let data;

            if (order.orderType === 'engagement') {
                data = await EngagementService.getEngagementDetails(orderId);
            } else {
                data = await OrdersService.getOrderDetails(orderId);
            }

            setSelectedOrder(data);
            return data;
        } catch (error) {
            message.error('Failed to fetch order details');
            return null;
        }
    };

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    return {
        unfulfilledOrders,
        fulfilledOrders,
        loading,
        selectedOrder,
        setSelectedOrder,
        updateOrderStatus,
        getOrderDetails,
        refreshOrders: fetchOrders
    };
};